import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';

import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Today as TodayIcon,
  TransferWithinAStation as TransferWithinAStationIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { APP_LINK } from '../common/menu/menuItems/menuList';
import ReusableTable from '../common/table/CommonTable';
import { fetchStaffCountRequest } from '../redux/dashboard/dashboardAction';
import {
  fetchHistoryPatients,
  fetchPatients,
} from '../redux/patient/patientAction';
import { fetchSecurityOfficers } from '../redux/secuityOfficer/securityAction';
import { fetchTransferPatient } from '../redux/TransferPatient/transferPatientAction';

const StaffDashboard = () => {
  const dispatch = useDispatch();
    const navigate = useNavigate()
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processedData, setProcessedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const securityOfficerState = useSelector(
    (state) => state?.securityOfficer?.fetchSecurityOfficers || {}
  );
  const securityOfficerList = securityOfficerState?.data?.data || [];

  const patientList = useSelector(
    (state) => state?.patients?.fetchListPatients || {}
  );
  const adminState = useSelector(
    (state) => state?.dashboard?.fetchStaffCount?.count || {}
  );

  const historyState = useSelector(
    (state) => state?.patients?.historyPatient || {}
  );
  const historyData = historyState?.data?.data || [];

  const transferState = useSelector(
    (state) => state?.transferPatient?.fetchListTransferPatient || {}
  );
  const adminStates = useSelector(
    (state) => state?.dashboard?.fetchAdminCount || {}
  );
   const countData = adminStates?.count?.count || {};
  const columnSecurity = [
    { id: 'security_serial_no', label: 'Guard ID', sortable: true },
    { id: 'security_gaurd_name', label: 'Name', sortable: true },
    { id: 'email', label: 'Email', sortable: true },
    { id: 'phone_number', label: 'Phone Number', sortable: true },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <Chip
          label={value === 1 ? 'Active' : 'Inactive'}
          color={value === 1 ? 'success' : 'warning'}
        />
      ),
    },
  ];
  
  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchPatients(currentPage));
      await dispatch(fetchStaffCountRequest(currentPage));
      await dispatch(fetchSecurityOfficers(currentPage));
      await dispatch(fetchHistoryPatients(currentPage));
      await dispatch(fetchTransferPatient(currentPage));
      setLoading(false);
    };
    fetchData();
  }, [dispatch, currentPage]);

  useEffect(() => {
    if (patientList.data && Array.isArray(patientList.data.data)) {
      const formattedData = patientList.data.data.map((patient) => ({
        id: patient.patient_serial_no,
        name: patient.patient_name,
        age: patient.age,
        status: patient.status || 'Active',
      }));
      setData(formattedData);
    } else {
      setData([]);
    }
  }, [patientList]);

  useEffect(() => {
    if (historyData.length > 0) {
      const processedData = historyData.map((item) => ({
        attendee: item.Attender?.attender_name || 'Unknown',
        attender_serial_no: item.Attender?.attender_serial_no || 'Unknown',
        status: item.attender_status === 2 ? 'In' : 'Out',
      }));

      setFilteredData(processedData);
      setLoading(false);
    }
  }, [historyData]);

  useEffect(() => {
    if (Array.isArray(transferState?.data?.data)) {
      const processed = transferState.data.data.map((item, index) => ({
        serialNo: index + 1,
        id: item.id,
        patientName: item.Patient?.patient_name || 'N/A',
        ipNumber: item.Patient?.ip_number || 'N/A',
        phoneNumber: item.Patient?.phone || 'N/A',
        staffName: item.Staff?.staff_name || 'N/A',
        oldBlock: item.old_Block?.block_name || 'N/A',
        oldFloor: item.old_Floor?.floor_name || 'N/A',
        oldWard: item.old_Ward?.ward_name || 'N/A',
        newBlock: item.new_Block?.block_name || 'N/A',
        newFloor: item.new_Floor?.floor_name || 'N/A',
        newWard: item.new_Ward?.ward_name || 'N/A',
        transferDate: new Date(item.createdAt).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        }),
        reason: item.reason || 'N/A',
      }));
      setProcessedData(processed);
      setFilteredData(processed);
    } else {
      setProcessedData([]);
      setFilteredData([]);
    }
  }, [transferState]);

  const columns = [
    { id: 'serialNo', label: 'S.No', sortable: true },
    { id: 'patientName', label: 'Patient Name', sortable: true },
    { id: 'phoneNumber', label: 'Phone Number', sortable: true },
    {
      id: 'oldBlock',
      label: 'Old Block',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="warning"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(255, 152, 0, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'oldFloor',
      label: 'Old Floor',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="warning"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(255, 152, 0, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'oldWard',
      label: 'Old Ward',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="warning"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(255, 152, 0, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'newBlock',
      label: 'New Block',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="success"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(76, 175, 80, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'newFloor',
      label: 'New Floor',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="success"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(76, 175, 80, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'newWard',
      label: 'New Ward',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color="success"
          size="small"
          sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)',
            color: 'white',
            boxShadow: '0 2px 5px rgba(76, 175, 80, 0.3)',
          }}
        />
      ),
    },
    {
      id: 'transferDate',
      label: 'Transfer Date',
      sortable: true,
      render: (value) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'medium',
            color: '#2c3e50',
          }}
        >
          {value}
        </Typography>
      ),
    },
  ];
  
  const statistics = {
    totalPatients: adminState?.count?.admissionCount || 0,
    activeAttendees: adminState?.count?.patientShift || 0,
    attendeesIn: adminState?.count?.inAttenderCount || 0,
    attendeesOut: adminState?.count?.outAttenderCount || 0,
    scanToday: adminState?.count?.patientShift || 0,
    todayPatients: countData.todayPatientsCount || 0,
    todayAttenders: countData.todayattenderCount || 0, 
  };

  const patientColumns = [
    { id: 'id', label: 'ID', sortable: true },
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      render: (value) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            color: '#2c3e50',
          }}
        >
          {value}
        </Typography>
      ),
    },
    { id: 'age', label: 'DOB', sortable: false },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      render: (status) => (
        <Box
          sx={{
            background:
              status === 'IN'
                ? 'linear-gradient(45deg, #4caf50 30%, #81c784 90%)'
                : 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
            borderRadius: 2,
            px: 1.5,
            py: 0.5,
            display: 'inline-block',
            color: 'white',
            fontWeight: 'bold',
            boxShadow:
              status === 'IN'
                ? '0 2px 5px rgba(76, 175, 80, 0.3)'
                : '0 2px 5px rgba(244, 67, 54, 0.3)',
          }}
        >
          {status}
        </Box>
      ),
    },
  ];

  const dashboardColors = {
    registered: 'linear-gradient(135deg, #6A11CB 0%, #2575FC 100%)',
    attendeesIn: 'linear-gradient(135deg, #00C853 0%, #64DD17 100%)',
    attendeesOut: 'linear-gradient(135deg, #FF3D00 0%, #FF8A65 100%)',
    transfers: 'linear-gradient(135deg, #FF9100 0%, #FFCA28 100%)',
    todayPatients: 'linear-gradient(135deg, #D500F9 0%, #E040FB 100%)',
    todayAttenders: 'linear-gradient(135deg, #00B0FF 0%, #40C4FF 100%)'
  };

  const handleGenerateReport = () => {
    navigate(APP_LINK.DAILYREPORT)
  };

  return (
    <Box
      sx={{
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
        minHeight: '100vh',
        pb: 4,
      }}
    >
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          p: { xs: 1, sm: 2, md: 3 },
          width: '100%',
        }}
      >
        {/* Count Boxes */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={2}>
            <Card
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                background: 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                sx={{
                  background: dashboardColors.registered,
                  pt: 2,
                  pb: 2,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  color="white"
                  variant="subtitle1"
                  fontWeight="bold"
                  fontSize={isMobile ? '0.8rem' : '1rem'}
                >
                  Currently Registered
                </Typography>
                <Avatar
                  sx={{
                    bgcolor: 'white',
                    color: '#6A11CB',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  <DashboardIcon />
                </Avatar>
              </Box>
              <CardContent sx={{ py: 3 }}>
                <Typography
                  variant="h3"
                  align="center"
                  color="#6A11CB"
                  sx={{
                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  {statistics.totalPatients}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} md={2}>
            <Card
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                background: 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                sx={{
                  background: dashboardColors.attendeesIn,
                  pt: 2,
                  pb: 2,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  color="white"
                  variant="subtitle1"
                  fontWeight="bold"
                  fontSize={isMobile ? '0.8rem' : '1rem'}
                >
                  Attendees IN
                </Typography>
                <Badge
                  badgeContent="IN"
                  color="success"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontWeight: 'bold',
                      fontSize: '0.6rem',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: 'white',
                      color: '#00C853',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    }}
                  >
                    <PeopleIcon />
                  </Avatar>
                </Badge>
              </Box>
              <CardContent sx={{ py: 3 }}>
                <Typography
                  variant="h3"
                  align="center"
                  color="#00C853"
                  sx={{
                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  {statistics.attendeesIn}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} md={2}>
            <Card
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                background: 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                sx={{
                  background: dashboardColors.attendeesOut,
                  pt: 2,
                  pb: 2,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  color="white"
                  variant="subtitle1"
                  fontWeight="bold"
                  fontSize={isMobile ? '0.8rem' : '1rem'}
                >
                  Attendees OUT
                </Typography>
                <Badge
                  badgeContent="OUT"
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontWeight: 'bold',
                      fontSize: '0.6rem',
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: 'white',
                      color: '#FF3D00',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    }}
                  >
                    <PeopleIcon />
                  </Avatar>
                </Badge>
              </Box>
              <CardContent sx={{ py: 3 }}>
                <Typography
                  variant="h3"
                  align="center"
                  color="#FF3D00"
                  sx={{
                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  {statistics.attendeesOut}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} md={2}>
            <Card
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                background: 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                sx={{
                  background: dashboardColors.transfers,
                  pt: 2,
                  pb: 2,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  color="white"
                  variant="subtitle1"
                  fontWeight="bold"
                  fontSize={isMobile ? '0.8rem' : '1rem'}
                >
                  Transfers Today
                </Typography>
                <Avatar
                  sx={{
                    bgcolor: 'white',
                    color: '#FF9100',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  <TransferWithinAStationIcon />
                </Avatar>
              </Box>
              <CardContent sx={{ py: 3 }}>
                <Typography
                  variant="h3"
                  align="center"
                  color="#FF9100"
                  sx={{
                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  {statistics.scanToday}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Today's Patient Count */}
          <Grid item xs={6} md={2}>
            <Card
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                background: 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                sx={{
                  background: dashboardColors.todayPatients,
                  pt: 2,
                  pb: 2,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  color="white"
                  variant="subtitle1"
                  fontWeight="bold"
                  fontSize={isMobile ? '0.8rem' : '1rem'}
                >
                  Today's Patients
                </Typography>
                <Avatar
                  sx={{
                    bgcolor: 'white',
                    color: '#D500F9',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  <TodayIcon />
                </Avatar>
              </Box>
              <CardContent sx={{ py: 3 }}>
                <Typography
                  variant="h3"
                  align="center"
                  color="#D500F9"
                  sx={{
                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  {statistics.todayPatients}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Today's Attender Count */}
          <Grid item xs={6} md={2}>
            <Card
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                height: '100%',
                background: 'white',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
              }}
            >
              <Box
                sx={{
                  background: dashboardColors.todayAttenders,
                  pt: 2,
                  pb: 2,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  color="white"
                  variant="subtitle1"
                  fontWeight="bold"
                  fontSize={isMobile ? '0.8rem' : '1rem'}
                >
                  Today's Attenders
                </Typography>
                <Avatar
                  sx={{
                    bgcolor: 'white',
                    color: '#00B0FF',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  }}
                >
                  <PeopleIcon />
                </Avatar>
              </Box>
              <CardContent sx={{ py: 3 }}>
                <Typography
                  variant="h3"
                  align="center"
                  color="#00B0FF"
                  sx={{
                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                    fontWeight: 'bold',
                    mb: 1,
                  }}
                >
                  {statistics.todayAttenders}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Report Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button
            variant="contained"
            // startIcon={<DownloadIcon />}
            onClick={handleGenerateReport}
            sx={{
              borderRadius: 2,
              py: 1.2,
              px: 3,
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(0, 176, 255, 0.3)',
              background: 'linear-gradient(45deg, #0288D1 30%, #29B6F6 90%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(45deg, #0277BD 30%, #0288D1 90%)',
                boxShadow: '0 6px 14px rgba(0, 176, 255, 0.4)',
                color: 'white',
              }
            }}
          >
            Report
          </Button>
        </Box>

        {/* Recent Patients and Current Security using ReusableTable */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          
   {/* Current Security */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                '& .MuiCardHeader-root': {
                  background: 'linear-gradient(135deg, #00C853 0%, #64DD17 100%)',
                  color: 'white',
                },
              }}
            >
              <ReusableTable
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ mr: 1 }} />
                    <Typography fontWeight="bold">Recent Patients</Typography>
                  </Box>
                }
                columns={patientColumns}
                data={data.slice(0, 5)}
                headerColor={"#000"}
                withPagination={false}
                withActions={false}
                rowsPerPage={4}
                customEmptyMessage="No patients registered"
                tableProps={{
                  sx: {
                    '& .MuiTableRow-root:hover': {
                      backgroundColor: 'rgba(0, 200, 83, 0.05)',
                    },
                    '& .MuiTableCell-head': {
                      fontWeight: 'bold',
                      backgroundColor: '#E8F5E9',
                    },
                  },
                }}
              />
            </Card>
          </Grid>
          {/* Current Security */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                '& .MuiCardHeader-root': {
                  background: 'linear-gradient(135deg, #00C853 0%, #64DD17 100%)',
                  color: 'white',
                },
              }}
            >
              <ReusableTable
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ mr: 1 }} />
                    <Typography fontWeight="bold">Current Security</Typography>
                  </Box>
                }
                columns={columnSecurity}
                data={securityOfficerList.slice(0, 5)}
                headerColor={"#000"}
                withPagination={false}
                withActions={false}
                rowsPerPage={4}
                customEmptyMessage="No security personnel registered"
                tableProps={{
                  sx: {
                    '& .MuiTableRow-root:hover': {
                      backgroundColor: 'rgba(0, 200, 83, 0.05)',
                    },
                    '& .MuiTableCell-head': {
                      fontWeight: 'bold',
                      backgroundColor: '#E8F5E9',
                    },
                  },
                }}
              />
            </Card>
          </Grid>
        </Grid>

        {/* Patient Transfer Activity */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                '& .MuiCardHeader-root': {
                  background: 'linear-gradient(135deg, #FF9100 0%, #FFCA28 100%)',
                  color: 'white',
                },
              }}
            >
              <ReusableTable
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PeopleIcon sx={{ mr: 1 }} />
                    <Typography fontWeight="bold">Patient Transfer Activity</Typography>
                  </Box>
                }
                columns={columns}
                data={processedData.slice(0, 5)}
                headerColor={"#000"}
                withPagination={true}
                withActions={false}
                rowsPerPage={5}
                customEmptyMessage="No recent activity"
                tableProps={{
                  sx: {
                    '& .MuiTableRow-root:hover': {
                      backgroundColor: 'rgba(245, 124, 0, 0.05)',
                    },
                    '& .MuiTableCell-head': {
                      fontWeight: 'bold',
                    },
                  },
                }}
              />
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
    
   
  );
};

export default StaffDashboard;
